import {useLocation, useNavigate} from "react-router-dom";
import {Center, CloseButton, Container, Group, Loader, Paper, Space, Title} from "@mantine/core";
import {
  adjustToTimeZone,
  getObjectId,
  useGetVehicleQuery,
  Vehicle,
  VehicleEntity,
  vehicleSelector
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo, useState} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {VehicleForm, VehicleFormDefaultValues, VehicleFormValues} from "../../components/form/VehicleForm/VehicleForm";
import {useVehicleMutation} from "../../hooks/useVehicleMutation";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {ProtectedPage} from "../../components/page/ProtectedPage/ProtectedPage";

type Props = {
  id?: string;
  copy?: boolean;
  formDefaults?: VehicleFormDefaultValues
}

export const VehicleFormPageRoute = () => {
  const {state} = useLocation();
  const id = state?.id;
  const copy = state?.copy;
  const formDefaults = state?.formDefaults;
  return (
    <VehicleFormPage
      id={id}
      copy={copy}
      formDefaults={formDefaults}
    />
  );
};

export const toFormDefaultsFromVehicle = (vehicle: VehicleEntity | undefined): VehicleFormDefaultValues => {
  return {
    name: vehicle?.name,
    barcode: vehicle?.barcode,
    vin: vehicle?.vin,
    company: vehicle?.companyId,
    doNotUse: vehicle?.doNotUse,
  };
};

export const VehicleFormPage = (props: Props) => {
  const {
    id,
    copy = false,
    formDefaults,
  } = props;

  const {regionId} = useApp()

  const navigate = useNavigate();
  const {t} = useTranslation(["common"]);

  const [access, setAccess] = useState(false);

  const selectVehicle = useMemo(() => {
    return vehicleSelector()
  }, [])

  const {currVehicle, isLoading} = useGetVehicleQuery(access ? (id ?? skipToken) : skipToken, {
    selectFromResult: (result) => ({
      currVehicle: selectVehicle(result),
      isLoading: result.isLoading
    })
  });

  const _formDefaults: VehicleFormDefaultValues = useMemo(() => {
    return formDefaults ? formDefaults : toFormDefaultsFromVehicle(currVehicle);
  }, [currVehicle, formDefaults]);

  const isEdit = useMemo(() => {
    return currVehicle && !copy
  }, [currVehicle, copy])

  const title: string = useMemo(() => {
    if (isEdit) {
      return t("page.entity.vehicle.form.title.edit", "Edit Vehicle", {ns: "common"});
    } else {
      return t("page.entity.vehicle.form.title.new", "New Vehicle", {ns: "common"});
    }
  }, [isEdit, t]);

  const {
    createVehicle,
    updateVehicle,
    isCreating,
    isUpdating,
  } = useVehicleMutation()

  const onVehicleSubmit = useCallback((formValues: VehicleFormValues) => {
    const patch: Partial<Vehicle> = {
      internalId: formValues.name,
      barcode: formValues.barcode,
      vin: formValues.vin,
      type: 'Semi-trailer Truck',
      companyId: getObjectId(formValues.company),
      doNotUse: formValues.doNotUse,
    }

    if (isEdit) {
      const vehicle: Partial<Vehicle> = {
        ...currVehicle.asTypeValue(),
        ...patch,
        lastModifiedDttm: adjustToTimeZone().getTime(),
      };
      updateVehicle(vehicle, {
        onSuccess: () => navigate(-1),
      })
    } else {
      const vehicle: Partial<Vehicle> = {
        ...patch,
      };
      createVehicle(vehicle, {
        onSuccess: (payload) => navigate(`/vehicles/profile?id=${payload.id}`, {replace: true}),
      })
    }
  }, [currVehicle, isEdit, createVehicle, updateVehicle, navigate]);

  return (
    <ProtectedPage
      permissions={[isEdit ? "vehicle_edit" : "vehicle_create"]}
      isLoading={isLoading}
      onAccess={setAccess}>
      {(classes) => (
        <Container className={classes.container} fluid={true} py="md">
          <Center>
            {isLoading && <Loader/>}
            {!isLoading && (
              <Paper miw={500} shadow="sm" p="md" py="md">
                <Group position="apart">
                  <Title align="center">
                    {title}
                  </Title>
                  <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
                </Group>
                <Space h="md"/>
                <VehicleForm
                  options={{
                    regionId: regionId,
                  }}
                  defaultValues={_formDefaults}
                  submitting={isCreating || isUpdating}
                  onSubmit={onVehicleSubmit}/>
              </Paper>
            )}
          </Center>
        </Container>
      )}
    </ProtectedPage>
  );
};

